import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";

const inputCls =
  "w-full bg-transparent border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/30 gold-focus transition-shadow";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-6" data-testid="forgot-password-page">
      <div className="grain-overlay" />
      <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="relative w-full max-w-md">
        <Link to="/" className="flex flex-col items-center gap-4 mb-12">
          <img src="/logo-mark.png" alt="MetrixWorks logo" className="h-16 w-16 bg-white p-1" />
          <span className="font-display text-2xl">MetrixWorks</span>
        </Link>
        <div className="bg-[#0A0A0A] border border-white/10 p-10">
          <p className="font-accent text-[11px] tracking-[0.3em] uppercase text-[#D4AF37] mb-3">Account recovery</p>
          <h1 className="font-display text-3xl tracking-tight mb-2">Forgot your password?</h1>
          <p className="text-sm text-white/50 mb-8">Enter your account email and we'll send a secure link to choose a new password. The link is valid for 1 hour.</p>
          {sent ? (
            <div className="border border-emerald-400/30 bg-emerald-400/5 px-4 py-4 text-sm text-emerald-300" data-testid="forgot-success">
              If an account exists for <span className="text-white">{email}</span>, a reset link is on its way. Check your inbox (and spam folder).
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="forgot-form">
              {error && <p data-testid="forgot-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/5 px-4 py-3">{error}</p>}
              <input data-testid="forgot-email-input" type="email" required placeholder="Email address" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <button data-testid="forgot-submit-button" disabled={loading} className="w-full border border-[#D4AF37] text-[#D4AF37] py-4 font-accent text-[11px] tracking-[0.25em] uppercase hover:bg-[#D4AF37] hover:text-black transition-colors disabled:opacity-50">
                {loading ? "Sending…" : "Send Reset Link"}
              </button>
            </form>
          )}
          <p className="mt-8 text-sm text-white/40">
            <Link to="/login" data-testid="forgot-back-to-login" className="text-[#D4AF37] hover:underline">Back to sign in</Link>
          </p>
        </div>
      </motion.div>
    </div>
  );
}
